import type { BuilderElement, GlobalConfig } from '@/utils/builder/types';
import { CSSProperties } from 'react';

const replaceColorTokensInString = (
  value: string,
  globalConfig: GlobalConfig
): string => {
  if (!value || !globalConfig.colors) return value;

  const protectedUrls: string[] = [];

  const valueWithoutUrls = value.replace(/url\(([^)]*)\)/gi, (match) => {
    const placeholder = `__BUILDER_URL_PLACEHOLDER_${protectedUrls.length}__`;
    protectedUrls.push(match);
    return placeholder;
  });

  const colorTokenKeys = Object.keys(globalConfig.colors);
  if (colorTokenKeys.length > 0) {
    const colorTokenRegex = new RegExp(`\\b(${colorTokenKeys.join('|')})\\b`, 'g');
    const replaced = valueWithoutUrls.replace(colorTokenRegex, (token) => {
      return globalConfig.colors[token] || token;
    });

    return replaced.replace(/__BUILDER_URL_PLACEHOLDER_(\d+)__/g, (_, idx) => {
      const index = Number(idx);
      return protectedUrls[index] ?? '';
    });
  }

  return valueWithoutUrls.replace(/__BUILDER_URL_PLACEHOLDER_(\d+)__/g, (_, idx) => {
    const index = Number(idx);
    return protectedUrls[index] ?? '';
  });
};

const normalizeCssValue = (value: unknown): unknown => {
  if (typeof value !== 'string') return value;

  const trimmed = value.trim();

  if (/^\d+(?:\.\d+)?x$/i.test(trimmed)) {
    return `${trimmed.slice(0, -1)}px`;
  }

  if (/^\d+(?:\.\d+)?pxrem$/i.test(trimmed)) {
    return `${trimmed.slice(0, -5)}px`;
  }

  if (/^\d+(?:\.\d+)?rempx$/i.test(trimmed)) {
    return `${trimmed.slice(0, -5)}rem`;
  }

  return value;
};

const compileStyleObject = (
  styleObject: Record<string, unknown> | undefined,
  globalConfig: GlobalConfig
): Record<string, unknown> => {
  if (!styleObject) return {};

  const compiled: Record<string, unknown> = {};

  for (const [key, rawValue] of Object.entries(styleObject)) {
    const normalizedValue = normalizeCssValue(rawValue);

    if (typeof normalizedValue === 'string') {
      compiled[key] = replaceColorTokensInString(normalizedValue, globalConfig);
    } else {
      compiled[key] = normalizedValue;
    }
  }

  if (
    typeof styleObject.fontStyle2 === 'string' &&
    (compiled.fontStyle === undefined || compiled.fontStyle === '')
  ) {
    compiled.fontStyle = styleObject.fontStyle2;
  }

  if (compiled.fontStyle === 'regular') {
    compiled.fontStyle = 'normal';
  }

  return compiled;
};

// Get typography style from global config if element references it
const getGlobalTypographyForElement = (
  element: BuilderElement,
  globalConfig: GlobalConfig
): {
  fontFamily?: string;
  fontSize?: string;
  fontStyle?: string;
  fontWeight?: string;
} | null => {
  if (!element.typography || !globalConfig.typography) return null;

  const typography = globalConfig.typography.find(
    (t) => t.name === element.typography
  );
  return typography || null;
};

// Compile elements with global config (resolve colors, typography)
export const compileElements = (
  elements: BuilderElement[],
  globalConfig: GlobalConfig
): BuilderElement[] => {
  return elements.filter(Boolean).map((element) => {
    const typographyStyle = getGlobalTypographyForElement(
      element,
      globalConfig
    );
    const elementStyles = compileStyleObject(
      (element.styles || {}) as Record<string, unknown>,
      globalConfig
    );

    const responsiveStyles = element.responsiveStyles
      ? {
          tablet: compileStyleObject(
            (element.responsiveStyles.tablet || {}) as Record<string, unknown>,
            globalConfig
          ) as BuilderElement['styles'],
          mobile: compileStyleObject(
            (element.responsiveStyles.mobile || {}) as Record<string, unknown>,
            globalConfig
          ) as BuilderElement['styles'],
        }
      : undefined;

    const hoverStyles = element.hoverStyles
      ? (compileStyleObject(
          element.hoverStyles as Record<string, unknown>,
          globalConfig
        ) as BuilderElement['hoverStyles'])
      : undefined;

    const responsiveHoverStyles = element.responsiveHoverStyles
      ? {
          tablet: compileStyleObject(
            (element.responsiveHoverStyles.tablet || {}) as Record<string, unknown>,
            globalConfig
          ) as BuilderElement['hoverStyles'],
          mobile: compileStyleObject(
            (element.responsiveHoverStyles.mobile || {}) as Record<string, unknown>,
            globalConfig
          ) as BuilderElement['hoverStyles'],
        }
      : undefined;

    const processedElement: BuilderElement = {
      ...element,
      styles: {
        ...(typographyStyle
          ? {
              fontFamily: typographyStyle.fontFamily,
              fontSize: typographyStyle.fontSize,
              fontStyle: typographyStyle.fontStyle,
              fontWeight: typographyStyle.fontWeight,
            }
          : {}),
        ...(elementStyles as BuilderElement['styles']),
      },
      responsiveStyles,
      hoverStyles,
      responsiveHoverStyles,
    };

    if (element.children) {
      processedElement.children = compileElements(
        element.children,
        globalConfig
      );
    }

    return processedElement;
  });
};

// Convert styles object to React CSSProperties
export const stylesToString = (
  styles: BuilderElement['styles'],
  viewMode: 'desktop' | 'tablet' | 'mobile' = 'desktop',
  responsiveStyles?: BuilderElement['responsiveStyles']
): CSSProperties => {
  let result: CSSProperties;

  if (viewMode === 'desktop') {
    result = { ...styles } as CSSProperties;
  } else {
    const responsiveStyle = responsiveStyles?.[viewMode] || {};
    result = { ...styles, ...responsiveStyle } as CSSProperties;
  }

  if (result.position === 'sticky' && styles?.stickyTopBackground) {
    result.backgroundColor = styles.stickyTopBackground;
  }

  return result;
};

// Convert hover styles object to React CSSProperties
export const hoverStylesToString = (
  hoverStyles: BuilderElement['hoverStyles'],
  viewMode: 'desktop' | 'tablet' | 'mobile' = 'desktop',
  responsiveHoverStyles?: BuilderElement['responsiveHoverStyles']
): CSSProperties => {
  if (!hoverStyles) return {};

  if (viewMode === 'desktop') {
    return hoverStyles as CSSProperties;
  }

  const responsiveHoverStyle = responsiveHoverStyles?.[viewMode] || {};
  return { ...hoverStyles, ...responsiveHoverStyle } as CSSProperties;
};
